import { useCallback, useEffect, useRef, useState } from 'react';
import {
  decodeServerEnvelope,
  encodeCallNext,
  encodeCloseShopForToday,
  encodeFinishTicket,
  encodeSkipTicket,
} from '../services/protoService';
import { generateUUID } from '../utils/uuid';

const WS_BASE = import.meta.env.VITE_NETTY_WS_URL || `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.hostname}:8080`;
const RETRY_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 10_000];

export function useStaffQueue(shopId, token) {
  const [queues, setQueues]     = useState({}); // { serviceId: [entries] }
  // The customer currently being served — set from the CallNextEvent so the
  // dashboard can render a "Now Serving" card with Done / Skip controls.
  const [calledTicket, setCalledTicket] = useState(null);
  const [wsStatus, setWsStatus] = useState('disconnected');
  // Exposed so the UI can show "Reconnecting (attempt 3)…" instead of a
  // silent disconnected state. Resets to 0 on successful connect.
  const [retryAttempt, setRetryAttempt] = useState(0);

  const wsRef    = useRef(null);
  const retryRef = useRef({ count: 0, timer: null });
  const activeRef = useRef(true);
  // connectRef points at the current `connect` closure so retry timers
  // and the online/visibility listeners always fire the latest closure
  // (which holds current shopId/token).
  const connectRef = useRef(null);

  const clearRetry = useCallback(() => {
    if (retryRef.current.timer) {
      clearTimeout(retryRef.current.timer);
      retryRef.current.timer = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!shopId || !token || !activeRef.current) return;

    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    setWsStatus('connecting');
    const url = `${WS_BASE}/queue?shopId=${encodeURIComponent(shopId)}&token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onopen = () => {
      retryRef.current.count = 0;
      setRetryAttempt(0);
      setWsStatus('connected');
    };

    ws.onmessage = async ({ data }) => {
      try {
        const env = await decodeServerEnvelope(data);
        if (env.queueUpdate) {
          const svcId = env.queueUpdate.serviceId || '_default';
          setQueues(prev => ({ ...prev, [svcId]: env.queueUpdate.entries || [] }));
        } else if (env.callNext) {
          setCalledTicket(env.callNext);
        }
      } catch (e) {
        console.error('Failed to decode ServerEnvelope', e);
      }
    };

    ws.onclose = () => {
      if (!activeRef.current) return;
      setWsStatus('disconnected');
      // Jitter ±40%: prevents every staff dashboard from reconnecting in
      // lock-step after a Netty restart and thundering the new node.
      const base = RETRY_DELAYS_MS[Math.min(retryRef.current.count, RETRY_DELAYS_MS.length - 1)];
      const delay = base * (0.6 + Math.random() * 0.8);
      retryRef.current.count++;
      setRetryAttempt(retryRef.current.count);
      retryRef.current.timer = setTimeout(() => connectRef.current?.(), delay);
    };

    ws.onerror = () => {};
  }, [shopId, token]);

  // Keep the ref pointing at the latest connect closure.
  useEffect(() => { connectRef.current = connect; }, [connect]);

  useEffect(() => {
    activeRef.current = true;
    connect();

    // Snap back immediately on network return or tab resume instead of
    // waiting out the full backoff timer (mirrors useQueue / useKiosk).
    const forceReconnect = () => {
      if (!activeRef.current) return;
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) return;
      clearRetry();
      retryRef.current.count = 0;
      connectRef.current?.();
    };
    const onOnline = () => forceReconnect();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') forceReconnect();
    };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      activeRef.current = false;
      clearRetry();
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [connect, clearRetry]);

  const send = useCallback(async (encodeFn, params) => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) return;
    try {
      const bytes = await encodeFn({ requestId: generateUUID(), ...params });
      wsRef.current.send(bytes);
    } catch (e) {
      console.error('Failed to encode message', e);
    }
  }, []);

  const callNext     = useCallback((serviceId) => send(encodeCallNext, { shopId, serviceId: serviceId || '' }), [send, shopId]);
  // Skip / Finish clear the "Now Serving" card optimistically when they target
  // the currently-called ticket: Netty sends the resulting Skip / Completed
  // event to the customer, not back to staff, so nothing else would clear it.
  const skipTicket   = useCallback((ticketUuid, serviceId) => {
    setCalledTicket((cur) => (cur && cur.ticketUuid === ticketUuid ? null : cur));
    return send(encodeSkipTicket, { shopId, ticketUuid, serviceId: serviceId || '' });
  }, [send, shopId]);
  const finishTicket = useCallback((ticketUuid, serviceId) => {
    setCalledTicket((cur) => (cur && cur.ticketUuid === ticketUuid ? null : cur));
    return send(encodeFinishTicket, { shopId, ticketUuid, serviceId: serviceId || '' });
  }, [send, shopId]);

  // Flatten all queues into a single list for backward compatibility
  const allEntries = Object.values(queues).flat();

  const closeShopForToday = useCallback(() => {
    // Collect every queue id the dashboard currently knows about (real
    // service ids plus the synthetic '_default' for no-service shops).
    // Netty expects the shop id for the default queue, not '_default'.
    const ids = Object.keys(queues).map((k) => (k === '_default' ? shopId : k));
    const queueIds = ids.length > 0 ? ids : [shopId];
    return send(encodeCloseShopForToday, { shopId, queueIds });
  }, [send, shopId, queues]);

  return { queues, queue: allEntries, calledTicket, wsStatus, retryAttempt, callNext, skipTicket, finishTicket, closeShopForToday };
}
